// externals
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

// libraries
import { Box } from '@interstate/components/Box';
import type { TextInputValue } from '@interstate/components/TextInput';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { Typography } from '@interstate/components/Typography';

// hooks
import { useFeesFormContext } from './useFeesFormContext';

// types utils constants
import { NOT_INCLUDED_IN_CALC_LABEL } from '../common/taxesAndFeesConsts';
import type { CellFinanceFeeNameProps } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { dealLockHooks } from '@makemydeal/dr-dash-components';

export const FinanceNameCell: FC<CellFinanceFeeNameProps> = ({ detail, index }) => {
    const dispatch = useDispatch();

    const [touched, setTouched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const prevNameValue = useRef<string>(detail.name);
    const isBlurInProgress = useRef<boolean>(false);

    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);

    const isLocked = dealLockHooks.useIsDealLocked();
    const {
        handleInputChange,
        getIsIncludeInCalc,
        setDisableFeeSubmit,
        commitFeeNameToRedux,
        handleSoftSaveOnEnterKey,
        handleInputBlur,
        shouldTriggerValidation,
        isOptionalDeskingMode
    } = useFeesFormContext();

    const isNameEmpty = !detail.name?.trim();
    const includedInCalc = getIsIncludeInCalc({ category: detail.category, includedInCalc: !!detail.includedInCalc });

    const handleBlur = (e: InterstateOnChangeEvent<TextInputValue>) => {
        isBlurInProgress.current = true;
        if (!isOptionalDeskingMode) setTouched(true);
        setIsFocused(false);
        validateAndCommitChanges();
        if (isDealScreenRedDotErrorsEnabled) {
            handleInputBlur({
                category: detail.category,
                field: 'feeName',
                index,
                isValid: e.isValid
            });
        }
    };

    const handleChange = (e: InterstateOnChangeEvent<string>) => {
        const value = e.target.value;
        if (isBlurInProgress.current) {
            isBlurInProgress.current = false;
            return;
        }
        dispatch(offerActionCreators.updatePaymentCallPendingState(prevNameValue.current !== value));
        setDisableFeeSubmit(value.trim() === '');
        handleInputChange({ category: detail.category, field: 'feeName', index, value });
    };

    const validateAndCommitChanges = () => {
        if (prevNameValue.current !== detail.name) {
            prevNameValue.current = detail.name;
            commitFeeNameToRedux();
        }
    };

    useEffect(() => {
        if (!isOptionalDeskingMode && shouldTriggerValidation) {
            setTouched(true);
        }
    }, [shouldTriggerValidation, isOptionalDeskingMode]);

    useEffect(() => {
        if (!isFocused) {
            prevNameValue.current = detail.name;
        }
    }, [detail.name, isFocused]);

    return (
        <Box display="flex" flexDirection="column" className="edit-fee--name-cell">
            <TextInput
                displayLabel={false}
                name="feeName"
                label="Name"
                data-testid={`edit-fee--name-cell-${detail.name}`}
                value={detail.name}
                disabled={isLocked || detail.isDeleted}
                onChange={handleChange}
                onBlur={(e) => handleBlur(e)}
                onFocus={() => {
                    isBlurInProgress.current = false;
                    setIsFocused(true);
                }}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                size={'sm'}
                onKeyDown={handleSoftSaveOnEnterKey(validateAndCommitChanges)}
                {...(!isOptionalDeskingMode && {
                    required: true,
                    errorMessage: touched && isNameEmpty ? 'Name is required' : undefined,
                    hasError: touched && isNameEmpty
                })}
            />
            {includedInCalc && (
                <Typography sx={{ color: 'gray' }} variant="body-sm">
                    {NOT_INCLUDED_IN_CALC_LABEL}
                </Typography>
            )}
        </Box>
    );
};
